import React, { useState, useRef, useEffect } from "react";
import { ArrowLeft } from "@speedoc/design-assets/build/icons/index.js";
import { useGlobalContext } from "../../utils/globalContext";
import { useMediaQuery } from "react-responsive";
import ContainerCard from "./containerCard";
import SectionHeading from "./sectionHeading";
import { Remarkable } from "remarkable";
import { padStartPolyfill } from "../../utils/strings";
import { LazyLoadImage } from "react-lazy-load-image-component";

const MIN_DISTANCE = 50;
const markdown = new Remarkable();

const DesktopSliderSection = ({ content = [] }) => {
  const { Trans } = useGlobalContext();
  const [index, setIndex] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [indices, setIndices] = useState([...Array(content.length).keys()]);
  const [disabled, setDisabled] = useState(false);
  const [imageAnimate, setImageAnimate] = useState(false);

  const firstAnimation = () =>
    new Promise(resolve => {
      setTimeout(() => {
        setFadeOut(false);
        setFadeIn(true);
        setImageAnimate(true);
        setIndices(prevState => [...prevState.slice(1, indices.length), prevState[0]]);
        setIndex(prevState => (prevState + 1) % content.length);
        resolve();
      }, 400);
    });

  const secondAnimation = () =>
    new Promise(resolve => {
      setTimeout(() => {
        setFadeIn(false);
        setImageAnimate(false);
        resolve();
      }, 400);
    });

  const nextSlide = async () => {
    setDisabled(true);
    setFadeOut(true);
    await firstAnimation();
    await secondAnimation();
    setDisabled(false);
  };

  return (
    <div id="sliderSection">
      <div className="sliderSectionLeft">
        <div className="dynamicSectionHeading">
          <div className="subtitle">{content.length && content[index].subtitle}</div>
          <div className="titleContainer">
            <hr />
            <h1
              className={`slider ${fadeOut ? "fadeOut" : ""} ${fadeIn ? "fadeIn" : ""} ${
                content.length && content[index].title.includes("<br") ? "" : "fixed-height"
              }`}>
              <Trans>{content.length && content[index].title}</Trans>
            </h1>
          </div>
          <div className={`description ${fadeOut ? "fadeOut" : ""}`}>
            <p
              className={`${fadeOut ? "fadeOut" : ""}`}
              dangerouslySetInnerHTML={{ __html: markdown.render(content.length && content[index].description) }}
            />
          </div>
        </div>
        <div className="sliderControlContainer">
          <hr />
          <div className="sliderControl">
            <div className="numbersBox">
              <div className="activeNumberContainer">
                {indices.slice(0, 2).map((number, i) => (
                  <p key={i} className={`number dynamic ${fadeOut ? "moveLeft" : ""}`}>
                    {padStartPolyfill({ content: number + 1, targetLength: 2, padString: "0" })}
                  </p>
                ))}
              </div>
              <p className="number">{padStartPolyfill({ content: content.length, targetLength: 2, padString: "0" })}</p>
            </div>
            <button className="leftArrow" onClick={nextSlide} disabled={disabled}>
              <ArrowLeft />
            </button>
          </div>
        </div>
      </div>
      <div className="sliderSectionRight">
        <LazyLoadImage
          src={content.length && content[index].image?.url}
          alt="slider right section"
          className={`${fadeOut ? "moveLeft" : ""} ${imageAnimate ? "reappear" : ""}`}
          effect="blur"
        />
      </div>
    </div>
  );
};

const MobileSliderCard = ({
  i,
  activeIndex,
  swiping,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  image,
  altImage,
  title,
  subtitle,
  description,
  contentLength,
  direction,
  list,
  handleSizeChange
}) => {
  const activeOrRest = i === activeIndex ? "active" : "rest";
  const swipingNext =
    swiping && i !== activeIndex && direction ? (direction === "next" ? "nextMoveLeft" : "moveRight") : "";
  const swipingActive =
    swiping && i === activeIndex && direction ? (direction === "next" ? "moveLeft" : "nextMoveRight") : "";
  const specialLastLower = activeIndex === 1 && i === contentLength - 1 ? "lower" : "";
  const className = `${activeOrRest} ${swipingNext} ${swipingActive} ${specialLastLower}`;

  return (
    <ContainerCard
      className={className}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      key={i}>
      <div ref={e => e && handleSizeChange && handleSizeChange(e.offsetHeight)} className="contentWrapper">
        <div className="imageWrapper">
          <LazyLoadImage src={image} alt={altImage} effect="blur" />
        </div>
        <div className="content">
          <SectionHeading title={title} subtitle={subtitle} />
          <p className="description" dangerouslySetInnerHTML={{ __html: markdown.render(description) }} />
        </div>
        <div className="pageNumber">
          <span className="number active">{padStartPolyfill({ content: i + 1, targetLength: 2, padString: "0" })}</span>
          {"/"}
          <span className="number active">
            {padStartPolyfill({ content: contentLength, targetLength: 2, padString: "0" })}
          </span>
        </div>
      </div>
    </ContainerCard>
  );
};

const MobileSliderSection = ({ content = [] }) => {
  const [swipe, setSwipe] = useState({});
  const [swiping, setSwiping] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState("");
  const [disabled, setDisabled] = useState(false);
  const sliderRef = useRef();

  useEffect(() => {
    setTimeout(() => {
      if (sliderRef.current) {
        Array.from(sliderRef.current.querySelectorAll(".contentWrapper")).map(ele => (ele.style.height = "100%"));
      }
    }, 100);
  });

  const handleTouchStart = e => {
    e.persist();

    if (swiping || disabled) return;

    if (e.touches && e.touches.length) {
      setSwipe({ xStart: e.touches[0].clientX });
    }
  };

  const handleTouchMove = e => {
    if (e.changedTouches && e.changedTouches.length) {
      const touch = e.changedTouches[0];
      if (swipe.xStart && touch.clientX) {
        if (swipe.xStart - touch.clientX > MIN_DISTANCE) {
          setDirection("next");
        } else if (swipe.xStart - touch.clientX < -MIN_DISTANCE) {
          setDirection("previous");
        }
        setSwipe(prevState => ({ ...prevState, xEnd: touch.clientX }));
      }
      setSwiping(true);
      setDisabled(true);
    }
  };

  const handleTouchEnd = e => {
    if (e.changedTouches && Math.abs(e.changedTouches[0].clientX - swipe.xStart) < 10) return;

    setTimeout(() => {
      setSwiping(false);
      const { xStart, xEnd } = swipe;
      if (xStart && xEnd) {
        setSwipe({});

        if (xStart - xEnd > MIN_DISTANCE) {
          setActiveIndex(prevState => (prevState + 1) % content.length);
        } else if (xStart - xEnd < -MIN_DISTANCE) {
          setActiveIndex(prevState => {
            if (prevState === 0) return content.length - 1;

            return (prevState - 1) % content.length;
          });
        }
      }
      setDirection("");
      setDisabled(false);
    }, 300);
  };

  return (
    <div id="sliderSectionMobile" className={activeIndex === content.length - 1 ? "restart" : ""} ref={sliderRef}>
      {content.map(({ image = {}, title, subtitle, description, list }, i) => (
        <MobileSliderCard
          i={i}
          activeIndex={activeIndex}
          swiping={swiping}
          handleTouchStart={handleTouchStart}
          handleTouchMove={handleTouchMove}
          handleTouchEnd={handleTouchEnd}
          handleSizeChange={newHeight => {
            let offsetHeight = sliderRef?.current?.offsetHeight || 0;
            if (sliderRef.current && newHeight > offsetHeight) {
              if (newHeight > offsetHeight) {
                sliderRef.current.style.height = newHeight + "px";
              }
            }
          }}
          image={image?.url}
          altImage={image?.alternativeText}
          title={title}
          subtitle={subtitle}
          description={description}
          contentLength={content?.length}
          direction={direction}
          list={list}
          key={i}
        />
      ))}
    </div>
  );
};

const SliderSection = ({ content }) => {
  const isMobile = useMediaQuery({ query: "(max-width: 576px)" });

  return content?.entries?.length && isMobile ? (
    <MobileSliderSection content={content.entries} />
  ) : (
    <DesktopSliderSection content={content.entries} />
  );
};

export default SliderSection;
